import { useState, useCallback } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { AutoRefreshToggle } from '../shared/AutoRefreshToggle';
import { RunsFilters } from './RunsFilters';
import { RunsList } from './RunsList';
import { useRuns } from '@/hooks/useMonitoringData';
import { useMonitoringStore } from '@/stores/monitoringStore';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '320px',
    height: '100%',
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
});

export const RunsSidebar = () => {
  const styles = useStyles();
  const [expandedRunIds, setExpandedRunIds] = useState<Set<string>>(new Set());

  // Store state and actions
  const {
    selectedRunId,
    selectedExecutionId,
    filters,
    autoRefreshEnabled,
    setSelectedRunId,
    setSelectedExecutionId,
    setFilters,
    clearFilters,
    toggleAutoRefresh,
  } = useMonitoringStore();

  // Fetch data
  const { data: runs, isLoading: runsLoading } = useRuns();

  const handleSelectRun = useCallback((runId: string) => {
    setSelectedRunId(runId);
  }, [setSelectedRunId]);

  const handleSelectExecution = useCallback((executionId: string) => {
    setSelectedExecutionId(executionId);
  }, [setSelectedExecutionId]);

  const handleToggleExpand = useCallback((runId: string) => {
    setExpandedRunIds((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  }, []);

  return (
    <div className={styles.container}>
      <AutoRefreshToggle
        enabled={autoRefreshEnabled}
        onToggle={toggleAutoRefresh}
      />
      <RunsFilters
        filters={filters}
        onFiltersChange={setFilters}
        onClearFilters={clearFilters}
      />
      <RunsList
        runs={runs ?? []}
        isLoading={runsLoading}
        selectedRunId={selectedRunId}
        selectedExecutionId={selectedExecutionId}
        expandedRunIds={expandedRunIds}
        onSelectRun={handleSelectRun}
        onSelectExecution={handleSelectExecution}
        onToggleExpand={handleToggleExpand}
      />
    </div>
  );
};
